import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import CustomerFormFields from "../components/customers/CustomerFormFields";
import PageLoader from "../components/common/PageLoader";
import { agentApi } from "../lib/apiClient";
import { showApiError, showSuccessAction } from "../lib/alerts";
import { unwrapData, downloadBlob } from "../lib/catalog";
import {
  EMPTY_CUSTOMER_FORM,
  createAgentCustomer,
  formFromCustomer,
  mapCustomerServerError,
  updateAgentCustomer,
  validateCustomerForm,
} from "../lib/customers";

const CUSTOMERS_CRUMB = { label: "Customers", href: "/customers" };

function readCustomer(payload) {
  const data = unwrapData(payload);
  return data.customer || data;
}

export default function CustomerFormPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(customerId);
  const [form, setForm] = useState(EMPTY_CUSTOMER_FORM);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      setForm(EMPTY_CUSTOMER_FORM);
      setErrors({});
      setFormError("");
      setLoading(false);
      return undefined;
    }

    let alive = true;
    async function load() {
      try {
        setLoading(true);
        const payload = await agentApi.get(`/travel-partner/customers/${customerId}`);
        if (!alive) return;
        const customer = readCustomer(payload);
        setForm(formFromCustomer(customer));
        setErrors({});
        setFormError("");
      } catch (fetchError) {
        if (alive) {
          showApiError(fetchError, "LOAD_CUSTOMER");
          navigate("/customers", { replace: true });
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [customerId, isEdit, navigate]);

  async function saveCustomer(event) {
    event.preventDefault();
    if (saving) return;

    const nextErrors = validateCustomerForm(form);
    setErrors(nextErrors);
    setFormError("");
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSaving(true);
      if (isEdit) {
        await updateAgentCustomer(customerId, form);
        showSuccessAction("UPDATE_CUSTOMER");
      } else {
        await createAgentCustomer(form);
        showSuccessAction("CREATE_CUSTOMER");
      }
      navigate("/customers");
    } catch (saveError) {
      const mapped = mapCustomerServerError(saveError);
      setFormError(mapped.formError);
      setErrors((current) => ({ ...current, ...mapped.fieldErrors }));
      showApiError(saveError, "SAVE_CUSTOMER");
    } finally {
      setSaving(false);
    }
  }

  async function viewPanCard() {
    if (!customerId || !form.has_pan_card) return;
    try {
      const blob = await agentApi.get(`/travel-partner/customers/${customerId}/pan-card`, {
        responseType: "blob",
      });
      downloadBlob(blob, form.pan_card_file_name || "pan-card");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_DOCUMENT");
    }
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Customers"
          title={isEdit ? "Edit customer" : "Add customer"}
          items={[DASHBOARD_CRUMB, CUSTOMERS_CRUMB, { label: isEdit ? "Edit" : "Add" }]}
          backTo="/customers"
          backStrict
        />

        {loading ? <PageLoader message="Loading customer..." /> : null}

        {!loading ? (
          <div className="row">
            <div className="col-12 col-xl-7">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-24">
                  <h5 className="mb-3">{isEdit ? "Update customer details" : "Create a new customer"}</h5>
                  <form onSubmit={saveCustomer}>
                    {formError ? (
                      <div className="alert alert-danger bg-danger-100 text-danger-600 border-danger-600 border-start-width-4-px border-top-0 border-end-0 border-bottom-0 px-16 py-12 mb-16 fw-semibold radius-4" role="alert">
                        {formError}
                      </div>
                    ) : null}
                    <CustomerFormFields
                      form={form}
                      errors={errors}
                      disabled={saving}
                      onChange={setForm}
                      variant="split"
                      onViewPanCard={isEdit ? viewPanCard : undefined}
                    />
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? "Saving..." : isEdit ? "Update customer" : "Create customer"}
                      </button>
                      <Link to="/customers" className="btn btn-outline-secondary">
                        Cancel
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
